import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { LanguageSwitch, useLanguage } from "@/context/LanguageContext";

export default function Login() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@scc.app");
  const [password, setPassword] = useState("admin123");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0A0A0A] text-white p-12 scc-grid-bg relative">
        <div className="absolute right-8 top-8"><LanguageSwitch dark /></div>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center">
            <img src="/comrocket-social-icon.jpg" alt="Comrocket" className="h-9 w-9 rounded object-cover" />
          </div>
          <div>
            <div className="font-display font-extrabold text-lg leading-none">Social Control Center</div>
            <div className="overline mt-1 !text-slate-500">{t("Agentur-Betriebssystem")}</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="font-display text-4xl font-extrabold leading-tight mb-4">{t("Redaktionsliste einwerfen. Prüfen. Veröffentlichen.")}</h1>
          <p className="text-slate-400 text-[15px] leading-relaxed">
            {t("Interne Plattform für Bulk-Import, Asset-Matching, Planung und kontrollierte Veröffentlichung über alle Kanäle – skalierbar für 100+ Accounts.")}
          </p>
        </div>
        <div className="flex gap-6 text-sm text-slate-500 font-mono">
          <span>Bulk-Import</span><span>{t("Multi-Mandant")}</span><span>{t("Analyse")}</span><span>{t("3 Modi")}</span>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 bg-[#F8F9FA]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center justify-between gap-2 mb-8">
            <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-white flex items-center justify-center"><img src="/comrocket-social-icon.jpg" alt="Comrocket" className="h-8 w-8 rounded object-cover" /></div>
            <span className="font-display font-extrabold">Social Control Center</span>
            </div><LanguageSwitch />
          </div>
          <h2 className="font-display text-2xl font-bold mb-1">{t("Anmelden")}</h2>
          <p className="text-slate-500 text-sm mb-8">{t("Interner Zugang für Administratoren & Redakteure.")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold text-slate-600">E-Mail</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold text-slate-600">{t("Passwort")}</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5" required />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full rounded-full bg-[#0A0A0A] hover:bg-[#212529] h-11 text-sm font-semibold">
              {busy ? t("Anmelden…") : <>{t("Anmelden")} <ArrowRight size={16} className="ml-1.5" /></>}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">{t("oder")}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <Button data-testid="login-google" variant="outline" onClick={googleLogin}
            className="w-full rounded-full h-11 text-sm font-semibold border-slate-300">
            <svg className="mr-2" width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
            {t("Mit Google anmelden")}
          </Button>

          <p className="text-xs text-slate-400 mt-6 text-center">
            {t("Demo-Zugang")}: <span className="font-mono">admin@scc.app / admin123</span>
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-4 text-xs text-slate-500">
            <Link className="underline underline-offset-4" to="/privacy">Privacy</Link>
            <Link className="underline underline-offset-4" to="/terms">Terms</Link>
            <a className="underline underline-offset-4" href="https://comrocket.de/impressum">Legal Notice</a>
          </div>
        </div>
      </div>
    </div>
  );
}
